import { useContext, useMemo, useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import createCachedSelector from 're-reselect';

import { useContextTypes } from '../../../hooks/use-context-types';
import { DisplayGridRecordPropType } from '../../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../../prop-types/display-grid-group-record';
import { GridHelperService } from '../../../services/grid-helper-service';

import { BUFFER_ROWS } from '../constants';
import { Context } from '../context';
import { RowContext } from '../row-context';
import { RowOffsetSubject } from '../observables/row-offset-subject';

import style from './index.module.less';

// Create a flattened array of all class names associated with each row in a,
// data set, along with some extra metadata about this particular record.
const getVirtualizedRowsMetadata = ({ data, isExpanded, pinned, isSelected, primaryRow, isGroupItem, isDisabled }) => {
  // Is this a single-order, single-line-item? If so, nothing else is necessary
  if (!Array.isArray(data)) {
    const className = classnames({
      [style.selected]: isSelected,
      [style.negativeMarginTop]: isSelected,
    });

    return [
      {
        allData: data,
        className,
        data,
        isSelected,
        isExpanded,
        isDisabled,
        key: data.groupById ?? data.id,
        primaryRow: data,
        shouldRenderToggler: !!data.groupById,
        isGroupItem: isGroupItem && !data.groupById,
      },
    ];
  }
  // This data object represents multiple rows in the grid output, because it's
  // comprised of multiple orders, an order with multiple line-items, or both.
  return data.reduce((metadata, combinedOrderOrLineItem, combinedOrderOrLineItemIndex) => {
    const isFirstRow = combinedOrderOrLineItemIndex === 0;
    const isLastRow = combinedOrderOrLineItemIndex === data.length - 1;

    // if this array element is not another array, then this item of the'data'
    // object is either part of a combined-order shipment, with this being a
    // single-line-item order, or is a line item of a single-order shipment.
    if (!Array.isArray(combinedOrderOrLineItem)) {
      // Reassign these to new variable names for clarity in this block.
      const lineItem = combinedOrderOrLineItem;
      const lineItemIndex = combinedOrderOrLineItemIndex;
      const isFirstOfType = isFirstRow;
      const isLastOfType = isLastRow;

      // The first time we come through here, calculate some first-row stuff.
      if (lineItemIndex === 0) {
        const className = classnames(style.expandable, style.visible, style.borderTop, {
          [style.borderBottom]: !isExpanded && !isGroupItem,
          [style.selected]: isSelected,
          [style.negativeMarginTop]: isSelected,
        });

        metadata.push({
          allData: data,
          data: lineItem,
          className,
          isExpanded,
          isSelected,
          isDisabled,
          key: lineItem.id,
          primaryRow,
          shouldRenderToggler: true,
          isGroupItem,
        });
      } else if (isExpanded) {
        // This only gets executed if we're in a multi-line-item order, and this
        // particular order happens to have its contents expanded in the grid.
        const className = classnames(style.expandable, style.visible, {
          [style.borderBottom]: isLastOfType && !isGroupItem,
          [style.selected]: isSelected,
          [style.negativeMarginTop]: isSelected,
        });

        metadata.push({
          allData: data,
          data: lineItem,
          className,
          isExpanded,
          isFirstOfType,
          isFirstRow,
          isSelected,
          isDisabled,
          key: `${primaryRow.id}_${lineItemIndex}`,
          primaryRow,
          renderCheckbox: false,
          isGroupItem,
        });
      }
    } else {
      // if this item is a nested array, then this is a combined-order
      // shipment, and this is a nested order with multiple line items.
      // Reassign these to new variable names for clarity in this block.
      const combinedOrder = combinedOrderOrLineItem;
      const combinedOrderIndex = combinedOrderOrLineItemIndex;

      combinedOrder.forEach((lineItem, lineItemIndex) => {
        const processLineItem = (actualLineItem = lineItem, actualLineItemIndex = lineItemIndex) => {
          const isFirstOfType = actualLineItemIndex === 0;
          const isLastOfType = !Array.isArray(lineItem) || actualLineItemIndex === lineItem.length - 1;

          // Only bother processing this record if it's the first one in the
          // combinedOrder, or the order isExpanded.
          if (combinedOrderIndex === 0 || isExpanded) {
            // If it's the first actualLineItem of this combinedOrder...
            if (combinedOrderIndex === 0 && actualLineItemIndex === 0) {
              const className = classnames(style.expandable, style.visible, {
                [style.borderBottom]: !isGroupItem && (!isExpanded || (isLastRow && isLastOfType)),
                [style.borderDashed]: !isGroupItem && !pinned && !isLastRow && isLastOfType,
                [style.borderTop]: !isGroupItem && isFirstRow,
                [style.selected]: isSelected,
                [style.negativeMarginTop]: isSelected && isFirstRow,
              });

              metadata.push({
                allData: data,
                data: actualLineItem,
                className,
                isExpanded,
                isFirstOfType,
                isSelected,
                isDisabled,
                key: `${actualLineItem.id}_${lineItemIndex}_${actualLineItemIndex}`,
                primaryRow,
                renderCheckbox: isFirstRow,
                shouldRenderToggler: isFirstRow,
                isGroupItem,
              });
            } else if (isExpanded) {
              // This is a subsequent actualLineItem, but this record isExpanded
              const className = classnames(style.expandable, style.visible, {
                [style.borderBottom]: !isGroupItem && isLastRow && isLastOfType,
                [style.borderDashed]: !pinned && !isLastRow && isLastOfType,
                [style.selected]: isSelected,
              });

              metadata.push({
                allData: data,
                data: actualLineItem,
                className,
                isExpanded,
                isFirstOfType,
                isFirstRow: false,
                isSelected,
                isDisabled,
                key: `${primaryRow.id}_${combinedOrderIndex}_${lineItemIndex}_${actualLineItemIndex}`,
                primaryRow,
                renderCheckbox: false,
                isGroupItem,
              });
            }
          }
        };

        if (Array.isArray(lineItem)) {
          lineItem.forEach(processLineItem);
        } else {
          processLineItem();
        }
      });
    }

    return metadata;
  }, []);
};

const extractRowData = createCachedSelector(
  ({ datum }) => datum,
  ({ isGroupItem }) => isGroupItem,
  ({ isExpanded }) => isExpanded,
  ({ pinned }) => pinned,
  ({ isSelected }) => isSelected,
  ({ isDisabled }) => isDisabled,
  (datum, isGroupItem = false, isExpanded = false, pinned, isSelected, isDisabled) => {
    const primaryRow = GridHelperService.getPrimaryRow(datum);

    const flattenedMetadata = getVirtualizedRowsMetadata({
      data: datum,
      isExpanded,
      pinned,
      isSelected,
      primaryRow,
      isGroupItem,
      isDisabled,
    });
    const flattenedDatum = GridHelperService.flatten(datum, isExpanded);
    return flattenedDatum.map((record, index) => ({
      data: record,
      metadata: flattenedMetadata[index],
    }));
  },
)(JSON.stringify);

/*
 * Create an array of all row records, expanded or not, that will fit
 * within the current defined height, plus any top and buttom buffer
 * rows, based on buffer settings.
 */
export const useVirtualizedRows = (pinned) => {
  useContextTypes('useVirtualizedRows', Context, {
    data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
    height: PropTypes.number.isRequired,
  });

  const { data, height } = useContext(Context);

  const { expanded, expandedGroupRows, selected, selectedGroupRows, disabledIds } = useContext(RowContext);
  const [rowOffset, setRowOffset] = useState(0);

  useEffect(() => {
    const subscription = RowOffsetSubject.subscribe((ro) => setRowOffset(ro));
    return () => subscription.unsubscribe();
  }, []);

  const rowHeight = GridHelperService.getRowHeight();
  const rowsPerPage = Math.ceil(height / rowHeight);

  // Memoize this value, as it's not likely to change very often. Calculate
  // it based on the available height, and the rowOffset value from scroll.
  const { visibleRows, topOffsetRows, bottomOffsetRows } = useMemo(() => {
    /*
     * Calculate the flattenedData set based on the flattened data of the
     * current record being processed, and the calculated metadata from our
     * helper function up above. Store them in separate properties to be
     * returned to consumers, which will spread the metadata props out to
     * its own children, as well as passing this record's data down to its
     * children as its own prop.
     */
    const flattenedData = data.reduce((acc, datum) => {
      const groupId = datum[0]?.groupById;
      const emptyRowPlaceholder = { isGroupbySeparator: true };
      if (groupId) {
        let selectedRecordCount = 0;
        const flattenedRecords = [];
        datum.forEach((gridRecord, index) => {
          const primaryRow = GridHelperService.getPrimaryRow(gridRecord);
          const flattenedRecord = extractRowData({
            datum: gridRecord,
            isGroupItem: true,
            isExpanded: expanded.has(primaryRow.id) || expandedGroupRows.has(gridRecord.groupById),
            isSelected: selected.has(primaryRow.id) || selectedGroupRows.has(gridRecord.groupById),
            pinned,
          });
          if (index > 0 && flattenedRecord[0].metadata.isSelected) {
            selectedRecordCount += 1;
          }
          flattenedRecords.push(...flattenedRecord);
        });
        flattenedRecords[0].data.selectedRecordCount = selectedRecordCount;
        if (expandedGroupRows.has(groupId)) {
          acc.push(...flattenedRecords);
        } else {
          acc.push(flattenedRecords[0]);
        }
        acc.push(emptyRowPlaceholder);
      } else {
        const primaryRow = GridHelperService.getPrimaryRow(datum);
        acc.push(
          ...extractRowData({
            datum,
            isExpanded: expanded.has(primaryRow.id) || expandedGroupRows.has(datum.groupById),
            isSelected: selected.has(primaryRow.id) || selectedGroupRows.has(datum.groupById),
            pinned,
            isDisabled: !!disabledIds[primaryRow.id],
          }),
        );
      }
      return acc;
    }, []);
    // Calculate the start and end indexes of this total page of data
    const start = Math.max(0, rowOffset - BUFFER_ROWS);
    const end = Math.min(flattenedData.length, rowOffset + rowsPerPage + BUFFER_ROWS);

    // Use the above data to calculate the top and bottom offset number of
    // rows, and the set of visibleRows to be rendered in this page of data.
    return {
      bottomOffsetRows: flattenedData.length - end,
      topOffsetRows: start,
      visibleRows: flattenedData.slice(start, end),
    };
  }, [data, rowOffset, rowsPerPage, expandedGroupRows, expanded, selected, selectedGroupRows, pinned, disabledIds]);

  // Return these offset values in pixels by multiplying the offset rows
  // by the rowHeight, along with the actual visibleRows to be rendered.
  return {
    bottomOffset: bottomOffsetRows * rowHeight,
    topOffset: topOffsetRows * rowHeight,
    rows: visibleRows,
  };
};
